import {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useNearScreen} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import ListCarousel from '@adv-mt/mt-list-carousel'
import IconArrowCircleDownOutline from '@adv-mt/mt-svg-icons/lib/ArrowCircleDownOutline'
import TagChip, {tagChipSizes, tagChipTypes} from '@adv-mt/mt-tag-chip'

import {CURRENT_YEAR, SIZES, TITLE_POSITIONS} from '../settings.js'
import {TrackingAddImpressions, useLatestModelsTracking} from './tracking.js'
const titleClass = 'mt-ListLatestModels-title'

// eslint-disable-next-line react/prop-types
const LatestModelsTitle = ({title, size, position}) => {
  const titleWrapperClass = 'mt-ListLatestModels-titleWrapper'
  const titleWrapperClasses = cx(titleWrapperClass, `${titleWrapperClass}--${position}`)
  const titleClasses = cx(titleClass, `${titleClass}--${size}`)

  return (
    <div className={titleWrapperClasses}>
      <TagChip label={CURRENT_YEAR} type={tagChipTypes.ACCENT} size={tagChipSizes.SMALL} />
      <span className={titleClasses}>{title}</span>
    </div>
  )
}

const getListCarouselLatestModels = ({items = [], onClick, i18n}) => {
  return items.map(({financedOfferPrice, image, make, makeId, model, modelId, offerPrice, price, url}) => {
    return {
      imageUrl: image,
      url,
      label: offerPrice && price && i18n.c(price),
      hasLabelStrikethrough: true,
      labelIcon: (
        <span className="mt-ListLatestModels--success">
          <IconArrowCircleDownOutline />
        </span>
      ),
      title: i18n.c(offerPrice || price),
      description: model,
      descriptionHighlight: make,
      onCardClick: () =>
        onClick({
          financedOfferPrice,
          make,
          makeId,
          model,
          modelId,
          offerPrice,
          price,
          url
        })
    }
  })
}

const ListLatestModels = ({
  models,
  listType,
  navigationOptions,
  size = SIZES.SMALL,
  title,
  titlePosition = TITLE_POSITIONS.LEFT
}) => {
  const {domain, i18n} = useContext(Context)
  const router = useRouter()
  const [isNear, outerRef] = useNearScreen()
  const config = domain.get('config')
  const {MODEL_DETAIL_PATH_PATTERN} = config.get('page')
  const hasNavigationOptions = Boolean(navigationOptions)
  const {trackModelClicked, trackModelDetailHomeClicked} = useLatestModelsTracking()

  const hasTitle = Boolean(title)

  const navigateToModelDetailHome = () =>
    trackModelDetailHomeClicked().then(() => {
      router.push(MODEL_DETAIL_PATH_PATTERN)
    })

  navigationOptions = hasNavigationOptions
    ? {
        ctaText: i18n.t('COMMON.CHECK_OFFERS'),
        onClick: navigateToModelDetailHome,
        url: MODEL_DETAIL_PATH_PATTERN,
        ...navigationOptions
      }
    : null

  const latestModelsTitle = () =>
    hasTitle ? <LatestModelsTitle size={size} position={titlePosition} title={title} /> : <></>

  const trackAndNavigateToModel = ({make, makeId, model, modelId, url}) => {
    trackModelClicked({make, makeId, model, modelId, url}).then(() => {
      router.push(url)
    })
  }

  const latestModelsCarouselItems = getListCarouselLatestModels({
    i18n,
    items: models,
    onClick: trackAndNavigateToModel
  })

  return (
    <div className="mt-ListLatestModels" ref={outerRef}>
      {isNear && listType ? <TrackingAddImpressions models={models} listType={listType} /> : <></>}
      <ListCarousel
        title={latestModelsTitle}
        items={latestModelsCarouselItems}
        size={size}
        navigationOptions={navigationOptions}
      />
    </div>
  )
}
ListLatestModels.Skeleton = () => {
  return <div className="mt-ListLatestModelsSkeleton"></div>
}
ListLatestModels.displayName = 'ListLatestModels'
ListLatestModels.propTypes = {
  /**
   * List of models to be displayed.
   */
  models: PropTypes.array,
  /**
   * Tracking listType.
   * If provided, it will be used to track add impressions.
   */
  listType: PropTypes.string,
  /**
   * Object with navigation card information.
   * If provided, a navigation card will be displayed.
   */
  navigationOptions: PropTypes.shape({
    ctaText: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
    position: PropTypes.string,
    url: PropTypes.string
  }),
  /**
   * Size of the component
   */
  size: PropTypes.oneOf(Object.values(SIZES)),
  /**
   * Title to be displayed.
   * If not provided, no title will be displayed.
   */
  title: PropTypes.string,
  /**
   * Position of the title. (Left by default)
   */
  titlePosition: PropTypes.oneOf(Object.values(TITLE_POSITIONS))
}

export default ListLatestModels

export {SIZES as listLatestModelsSizes, TITLE_POSITIONS as listCarouselPositions}
